/* ============================================================
   ManMade — Homepage JavaScript
   Hero carousel · Video modal · Horizontal snappers · FAQ
   accordion · Scroll-reveals (IntersectionObserver, gated by
   prefers-reduced-motion).
   ============================================================ */

'use strict';

/* ----------------------------------------------------------
   1. HERO CAROUSEL
   ---------------------------------------------------------- */
function initHomeHero() {
  var hero = document.querySelector('[data-mm-hero-track]');
  if (!hero || hero.__mmBound) return;
  hero.__mmBound = true;

  var slides = Array.from(hero.querySelectorAll('.mm-home-hero__slide'));
  if (slides.length < 2) return;

  var section = hero.closest('.mm-home-hero');
  var dots    = Array.from(section.querySelectorAll('[data-mm-hero-dot]'));
  var prevBtn = section.querySelector('[data-mm-hero-prev]');
  var nextBtn = section.querySelector('[data-mm-hero-next]');
  var pauseBtn = section.querySelector('[data-mm-hero-pause]');
  var pauseIcon = section.querySelector('[data-mm-hero-pause-icon]');
  var pauseLabel = section.querySelector('[data-mm-hero-pause-label]');
  var statusRegion = section.querySelector('[data-mm-hero-status]');

  // Honor reduced motion: disable autoplay entirely (WCAG 2.2.2, 2.3.3).
  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var current = 0;
  var autoplay = hero.dataset.mmAutoplay !== 'false' && !prefersReducedMotion;
  var delay = parseInt(hero.dataset.mmAutoplayDelay, 10) || 7000;
  var timer = null;
  var hovering = false;
  var userPaused = false;

  function announce(idx) {
    if (!statusRegion) return;
    statusRegion.textContent = 'Slide ' + (idx + 1) + ' of ' + slides.length;
  }

  function go(idx) {
    var next = (idx + slides.length) % slides.length;
    slides[current].setAttribute('aria-hidden', 'true');
    slides[next].removeAttribute('aria-hidden');
    if (dots[current]) {
      dots[current].setAttribute('aria-selected', 'false');
      dots[current].setAttribute('tabindex', '-1');
    }
    if (dots[next]) {
      dots[next].setAttribute('aria-selected', 'true');
      dots[next].setAttribute('tabindex', '0');
    }
    current = next;
    announce(current);
  }

  function tick() {
    if (hovering || userPaused || document.hidden) return;
    go(current + 1);
  }
  function start() { stop(); if (autoplay && !userPaused) timer = setInterval(tick, delay); }
  function stop()  { if (timer) { clearInterval(timer); timer = null; } }

  function setPaused(p) {
    userPaused = p;
    if (pauseBtn) {
      pauseBtn.setAttribute('aria-pressed', p ? 'true' : 'false');
      if (pauseIcon) pauseIcon.textContent = p ? '▶' : '❚❚';
      if (pauseLabel) pauseLabel.textContent = p ? 'Play slideshow' : 'Pause slideshow';
      pauseBtn.setAttribute('aria-label', p ? 'Play slideshow' : 'Pause slideshow');
    }
    if (p) stop(); else start();
  }

  if (prevBtn) prevBtn.addEventListener('click', function() { go(current - 1); if (!userPaused) start(); });
  if (nextBtn) nextBtn.addEventListener('click', function() { go(current + 1); if (!userPaused) start(); });
  if (pauseBtn) {
    pauseBtn.addEventListener('click', function() { setPaused(!userPaused); });
  }
  dots.forEach(function(dot, i) {
    dot.addEventListener('click', function() { go(i); if (!userPaused) start(); });
    // Arrow-key navigation for the tablist (WCAG 2.1.1 Keyboard).
    dot.addEventListener('keydown', function(e) {
      var dir = 0;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') dir = 1;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') dir = -1;
      else if (e.key === 'Home') { e.preventDefault(); dots[0].focus(); go(0); return; }
      else if (e.key === 'End')  { e.preventDefault(); dots[dots.length-1].focus(); go(dots.length-1); return; }
      else return;
      e.preventDefault();
      var target = (i + dir + dots.length) % dots.length;
      dots[target].focus();
      go(target);
    });
  });

  // Pause autoplay on hover and focus-within for keyboard users.
  section.addEventListener('mouseenter', function() { hovering = true; stop(); });
  section.addEventListener('mouseleave', function() { hovering = false; if (!userPaused) start(); });
  section.addEventListener('focusin',  function() { hovering = true; stop(); });
  section.addEventListener('focusout', function(e) {
    if (!section.contains(e.relatedTarget)) { hovering = false; if (!userPaused) start(); }
  });

  document.addEventListener('visibilitychange', function() {
    if (document.hidden) stop();
    else if (autoplay && !userPaused) start();
  });

  // Touch swipe — horizontal-dominant only, so a vertical page scroll that
  // starts on the hero never flips the slide; a short quick flick counts too.
  var startX = null, startY = 0, startT = 0;
  hero.addEventListener('touchstart', function(e) {
    if (e.touches.length !== 1) { startX = null; return; }
    startX = e.touches[0].clientX;
    startY = e.touches[0].clientY;
    startT = e.timeStamp;
  }, { passive: true });
  hero.addEventListener('touchend', function(e) {
    if (startX === null) return;
    var dx = e.changedTouches[0].clientX - startX;
    var dy = e.changedTouches[0].clientY - startY;
    var speed = Math.abs(dx) / Math.max(1, e.timeStamp - startT);
    startX = null;
    if (Math.abs(dx) < Math.abs(dy) * 1.2) return;
    if (Math.abs(dx) > 40 || (Math.abs(dx) > 20 && speed > 0.4)) {
      go(dx < 0 ? current + 1 : current - 1);
      if (!userPaused) start();
    }
  }, { passive: true });
  hero.addEventListener('touchcancel', function() { startX = null; }, { passive: true });

  start();
}

/* ----------------------------------------------------------
   2. VIDEO MODAL
   ---------------------------------------------------------- */
function initVideoModal() {
  var triggers = document.querySelectorAll('[data-mm-video-open]');
  if (!triggers.length) return;

  var modal = document.getElementById('mm-video-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'mm-video-modal';
    modal.className = 'mm-video-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Video player');
    modal.hidden = true;
    modal.innerHTML =
      '<div class="mm-video-modal__scrim" data-mm-video-close></div>' +
      '<div class="mm-video-modal__dialog">' +
        '<button class="mm-video-modal__close" data-mm-video-close aria-label="Close video">×</button>' +
        '<div class="mm-video-modal__player" data-mm-video-player></div>' +
      '</div>';
    document.body.appendChild(modal);
    modal.addEventListener('click', function(e) {
      if (e.target.matches('[data-mm-video-close]')) close();
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && !modal.hidden) close();
    });
  }

  var player = modal.querySelector('[data-mm-video-player]');

  function buildEmbed(url) {
    if (!url) return '';
    if (/youtu\.?be/.test(url)) {
      var id = (url.split('v=')[1] || url.split('/').pop() || '').split('&')[0].split('?')[0];
      return '<iframe src="https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0" ' +
             'allow="autoplay; fullscreen; picture-in-picture" allowfullscreen ' +
             'title="Video"></iframe>';
    }
    if (/vimeo/.test(url)) {
      var vid = (url.split('vimeo.com/')[1] || '').split('?')[0].split('/').pop();
      return '<iframe src="https://player.vimeo.com/video/' + vid + '?autoplay=1" ' +
             'allow="autoplay; fullscreen; picture-in-picture" allowfullscreen title="Video"></iframe>';
    }
    return '<video src="' + url + '" controls autoplay playsinline></video>';
  }

  function open(url) {
    if (!url) return;
    player.innerHTML = buildEmbed(url);
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
  }
  function close() {
    modal.hidden = true;
    player.innerHTML = '';
    document.body.style.overflow = '';
  }

  triggers.forEach(function(t) {
    if (t.__mmBound) return;
    t.__mmBound = true;
    t.addEventListener('click', function(e) {
      var url = t.dataset.mmVideoOpen;
      if (!url) return;
      e.preventDefault();
      open(url);
    });
  });
}

/* ----------------------------------------------------------
   3. HORIZONTAL SCROLL SNAPPERS (ingredients, experts, blog)
   ---------------------------------------------------------- */
function initSnappers() {
  document.querySelectorAll('[data-mm-snapper]').forEach(function(snap) {
    if (snap.__mmBound) return;
    snap.__mmBound = true;
    var prev = snap.parentElement.querySelector('[data-mm-snap-prev]');
    var next = snap.parentElement.querySelector('[data-mm-snap-next]');

    function step() {
      var first = snap.firstElementChild;
      if (!first) return 240;
      var styles = getComputedStyle(snap);
      var gap = parseFloat(styles.columnGap || styles.gap || '12') || 12;
      return first.getBoundingClientRect().width + gap;
    }
    function update() {
      if (!prev || !next) return;
      var max = snap.scrollWidth - snap.clientWidth - 1;
      prev.disabled = snap.scrollLeft <= 0;
      next.disabled = snap.scrollLeft >= max;
    }

    if (prev) prev.addEventListener('click', function() { snap.scrollBy({ left: -step(), behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function() { snap.scrollBy({ left:  step(), behavior: 'smooth' }); });
    // Coalesce scroll-driven arrow-state updates to one layout read per frame.
    var rafId = 0;
    function onScroll() {
      if (rafId) return;
      rafId = requestAnimationFrame(function() { rafId = 0; update(); });
    }
    snap.addEventListener('scroll', onScroll, { passive: true });
    update();
  });
}

/* ----------------------------------------------------------
   4. FAQ ACCORDION (exclusive)
   ---------------------------------------------------------- */
function initHomeFaq() {
  if (window.MMSpring) return; // mm-spring.js animates these and closes siblings itself
  document.querySelectorAll('[data-mm-home-faq]').forEach(function(list) {
    if (list.__mmBound) return;
    list.__mmBound = true;
    var items = Array.from(list.querySelectorAll('details'));
    items.forEach(function(d) {
      d.addEventListener('toggle', function() {
        if (d.open) {
          items.forEach(function(o) { if (o !== d && o.open) o.removeAttribute('open'); });
        }
      });
    });
  });
}

// Every init skips elements it already bound, so this re-runs safely when
// sections arrive later (theme editor, or deferred sections in theme.liquid).
function initHome() {
  initHomeHero();
  initVideoModal();
  initSnappers();
  initHomeFaq();
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initHome);
else initHome();
document.addEventListener('shopify:section:load', initHome);
